import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { MapPin, Clock, Users, ChevronRight, Loader2, Navigation } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// ADA Sections with coordinates
const ADA_SECTIONS = [
  { id: 'abidjan-cocody', name: 'Cocody', city: 'Abidjan', lat: 5.3599, lng: -3.9854, contact: '+225 07 XX XX XX XX' },
  { id: 'abidjan-plateau', name: 'Plateau', city: 'Abidjan', lat: 5.3189, lng: -4.0225, contact: '+225 07 XX XX XX XX' },
  { id: 'abidjan-yopougon', name: 'Yopougon', city: 'Abidjan', lat: 5.3389, lng: -4.0725, contact: '+225 07 XX XX XX XX' },
  { id: 'yamoussoukro', name: 'Yamoussoukro', city: 'Yamoussoukro', lat: 6.8276, lng: -5.2893, contact: '+225 07 XX XX XX XX' },
  { id: 'bouake', name: 'Bouaké', city: 'Bouaké', lat: 7.6939, lng: -5.0308, contact: '+225 07 XX XX XX XX' },
  { id: 'korhogo', name: 'Korhogo', city: 'Korhogo', lat: 9.4580, lng: -5.6295, contact: '+225 07 XX XX XX XX' },
  { id: 'san-pedro', name: 'San-Pédro', city: 'San-Pédro', lat: 4.7485, lng: -6.6363, contact: '+225 07 XX XX XX XX' },
  { id: 'daloa', name: 'Daloa', city: 'Daloa', lat: 6.8774, lng: -6.4502, contact: '+225 07 XX XX XX XX' },
  { id: 'man', name: 'Man', city: 'Man', lat: 7.4125, lng: -7.5536, contact: '+225 07 XX XX XX XX' },
  { id: 'gagnoa', name: 'Gagnoa', city: 'Gagnoa', lat: 6.1319, lng: -5.9506, contact: '+225 07 XX XX XX XX' },
  { id: 'divo', name: 'Divo', city: 'Divo', lat: 5.8372, lng: -5.3572, contact: '+225 07 XX XX XX XX' },
  { id: 'bondoukou', name: 'Bondoukou', city: 'Bondoukou', lat: 8.0404, lng: -2.8000, contact: '+225 07 XX XX XX XX' },
  // Diaspora sections
  { id: 'paris', name: 'Paris', city: 'Paris', country: 'France', lat: 48.8566, lng: 2.3522, contact: '+33 6 XX XX XX XX', isDiaspora: true },
  { id: 'bruxelles', name: 'Bruxelles', city: 'Bruxelles', country: 'Belgique', lat: 50.8503, lng: 4.3517, contact: '+32 4 XX XX XX XX', isDiaspora: true },
  { id: 'new-york', name: 'New York', city: 'New York', country: 'USA', lat: 40.7128, lng: -74.0060, contact: '+1 XXX XXX XXXX', isDiaspora: true },
  { id: 'montreal', name: 'Montréal', city: 'Montréal', country: 'Canada', lat: 45.5017, lng: -73.5673, contact: '+1 XXX XXX XXXX', isDiaspora: true },
];

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Find nearest section
const findNearestSection = (userLat, userLng) => {
  let nearest = null;
  let minDistance = Infinity;
  
  ADA_SECTIONS.forEach(section => {
    const distance = calculateDistance(userLat, userLng, section.lat, section.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = { ...section, distance };
    }
  });
  
  return nearest;
};

const UserLocationWidget = ({ variant = 'compact' }) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [location, setLocation] = useState(null);
  const [nearestSection, setNearestSection] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const t = {
    fr: {
      yourLocation: 'Votre position',
      nearestSection: 'Section ADA la plus proche',
      localTime: 'Heure locale',
      distance: 'à',
      km: 'km',
      contact: 'Contacter',
      join: 'Rejoindre cette section',
      loading: 'Localisation...',
      locationDenied: 'Localisation non autorisée',
      enableLocation: 'Activer la localisation',
      inCoteIvoire: 'en Côte d\'Ivoire',
      diaspora: 'Diaspora',
      findSection: 'Trouver ma section'
    },
    en: {
      yourLocation: 'Your location',
      nearestSection: 'Nearest ADA section',
      localTime: 'Local time',
      distance: 'at',
      km: 'km',
      contact: 'Contact',
      join: 'Join this section',
      loading: 'Locating...',
      locationDenied: 'Location not authorized',
      enableLocation: 'Enable location',
      inCoteIvoire: 'in Côte d\'Ivoire',
      diaspora: 'Diaspora',
      findSection: 'Find my section'
    },
    pt: {
      yourLocation: 'A sua localização',
      nearestSection: 'Secção ADA mais próxima',
      localTime: 'Hora local',
      distance: 'a',
      km: 'km',
      contact: 'Contactar',
      join: 'Aderir a esta secção',
      loading: 'A localizar...',
      locationDenied: 'Localização não autorizada',
      enableLocation: 'Ativar localização',
      inCoteIvoire: 'na Costa do Marfim',
      diaspora: 'Diáspora',
      findSection: 'Encontrar a minha secção'
    }
  };

  const content = t[language] || t.fr;

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get user location
  useEffect(() => {
    const getLocation = async () => {
      setLoading(true);
      
      // First, try IP-based geolocation (no permission needed, instant)
      try {
        const response = await fetch('https://ipapi.co/json/', { 
          signal: AbortSignal.timeout(5000) 
        });
        
        if (!response.ok) throw new Error('IP API failed');
        
        const data = await response.json();
        
        if (data.latitude && data.longitude) {
          setLocation({
            lat: data.latitude,
            lng: data.longitude,
            city: data.city,
            country: data.country_name,
            countryCode: data.country_code
          });
          
          const nearest = findNearestSection(data.latitude, data.longitude);
          setNearestSection(nearest);
          setLoading(false);
          
          // Optionally, try to get more precise location with browser geolocation
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              async (position) => {
                const { latitude, longitude } = position.coords;
                
                // Update with precise location
                setLocation(prev => ({
                  ...prev,
                  lat: latitude,
                  lng: longitude
                }));
                
                // Update nearest section with precise location
                const nearest = findNearestSection(latitude, longitude);
                setNearestSection(nearest);
              },
              () => {}, // Ignore errors
              { timeout: 5000, enableHighAccuracy: false }
            );
          }
        } else {
          throw new Error('No location data');
        }
      } catch (e) {
        // IP geolocation failed - use default Abidjan location for demo
        // In production, this would show an error or prompt for manual input
        const defaultLocation = {
          lat: 5.3599,
          lng: -3.9854,
          city: 'Abidjan',
          country: 'Côte d\'Ivoire',
          countryCode: 'CI'
        };
        
        setLocation(defaultLocation);
        const nearest = findNearestSection(defaultLocation.lat, defaultLocation.lng);
        setNearestSection(nearest);
        setLoading(false);
      }
    };

    getLocation();
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDistance = (km) => {
    if (km < 1) return `${Math.round(km * 1000)} m`;
    if (km < 10) return `${km.toFixed(1)} ${content.km}`;
    return `${Math.round(km)} ${content.km}`;
  };

  // Compact version for header
  if (variant === 'compact') {
    return (
      <div className="relative">
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            isDark 
              ? 'bg-white/10 hover:bg-white/15 text-white/80' 
              : 'bg-[#5B2D8E]/10 hover:bg-[#5B2D8E]/15 text-[#5B2D8E]'
          }`}
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <MapPin className="w-3.5 h-3.5" />
          )}
          <span>
            {loading ? content.loading : location?.city || content.findSection}
          </span>
          <Clock className="w-3.5 h-3.5 ml-1" />
          <span className="font-mono">{formatTime(currentTime)}</span>
        </button>

        <AnimatePresence>
          {expanded && nearestSection && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className={`absolute top-full right-0 mt-2 w-72 p-4 rounded-lg shadow-xl z-50 ${
                isDark ? 'bg-[#1A0F2E] border border-white/10' : 'bg-white border border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg ${nearestSection.isDiaspora ? 'bg-[#0D9488]/20' : 'bg-[#5B2D8E]/20'}`}>
                  <Navigation className={`w-5 h-5 ${nearestSection.isDiaspora ? 'text-[#0D9488]' : 'text-[#5B2D8E]'}`} />
                </div>
                <div className="flex-1">
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {content.nearestSection}
                  </p>
                  <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Section {nearestSection.name}
                  </p>
                  <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {content.distance} {formatDistance(nearestSection.distance)}
                    {nearestSection.isDiaspora && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#0D9488]/20 text-[#0D9488]">
                        {content.diaspora}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              <Link
                to="/rejoindre"
                className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-[#F7C948] text-[#0A0510] font-semibold text-sm rounded-lg hover:bg-[#E5A700] transition-colors"
              >
                {content.join}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Full version for homepage
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`p-6 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-lg`}
    >
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-4">
          <Loader2 className="w-5 h-5 animate-spin text-[#5B2D8E]" />
          <span className={isDark ? 'text-white/70' : 'text-gray-600'}>{content.loading}</span>
        </div>
      ) : error ? (
        <div className="text-center py-4">
          <MapPin className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.locationDenied}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-[#5B2D8E] hover:underline"
          >
            {content.enableLocation}
          </button>
        </div>
      ) : (
        <>
          {/* Location & Time Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#5B2D8E]" />
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {location?.city || content.yourLocation}
                </p>
                {location?.country && (
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {location.country}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#F7C948]" />
              <span className={`font-mono text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatTime(currentTime)}
              </span>
            </div>
          </div>

          {/* Nearest Section */}
          {nearestSection && (
            <div className={`p-4 rounded-lg ${isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/5'}`}>
              <p className={`text-xs font-medium mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {content.nearestSection}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    nearestSection.isDiaspora ? 'bg-[#0D9488]' : 'bg-[#5B2D8E]'
                  }`}>
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Section {nearestSection.name}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      {content.distance} {formatDistance(nearestSection.distance)}
                      {nearestSection.isDiaspora && (
                        <span className="ml-2 text-[#0D9488]">• {content.diaspora}</span>
                      )}
                    </p>
                  </div>
                </div>
                <Link
                  to="/rejoindre"
                  className="px-4 py-2 bg-[#F7C948] text-[#0A0510] font-semibold text-sm rounded-lg hover:bg-[#E5A700] transition-colors flex items-center gap-1"
                >
                  {content.join}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default UserLocationWidget;
